/**
 * Write helpers for the outbound-delivery control plane.
 *
 * Each returns `undefined` on success or a `{ error, violations }` the form
 * shows, and fires `revalidatePath` so any list on screen reloads — the same
 * shape the roles/masters actions use.
 */

import {
  ApiError,
  createClientFeedTransform,
  createDeliveryAssignment,
  createDeliveryClient,
  createFeedPackage,
  deleteClientFeedTransform,
  deleteDeliveryAssignment,
  updateClientFeedTransform,
  updateDeliveryAssignment,
  updateDeliveryClient,
  updateFeedDefinition,
  updateFeedPackage,
} from "@/lib/api/client";
import type {
  ClientFeedTransformCreatePayload,
  ClientFeedTransformUpdatePayload,
  DeliveryAssignmentCreatePayload,
  DeliveryAssignmentUpdatePayload,
  DeliveryClientCreatePayload,
  DeliveryClientUpdatePayload,
  FeedDefinitionUpdatePayload,
  FeedPackageCreatePayload,
  FeedPackageUpdatePayload,
} from "@/lib/api/types";
import { revalidatePath } from "@/lib/revalidate";

export type ActionResult = { error: string; violations: string[] } | undefined;

async function run(work: () => Promise<unknown>, fallback: string): Promise<ActionResult> {
  try {
    await work();
  } catch (error) {
    if (error instanceof ApiError) return { error: error.message, violations: error.violations };
    return { error: fallback, violations: [] };
  }
  revalidatePath("/delivery");
  return undefined;
}

export const createClientAction = (p: DeliveryClientCreatePayload) =>
  run(() => createDeliveryClient(p), "Unexpected error creating the client.");
export const updateClientAction = (id: string, p: DeliveryClientUpdatePayload) =>
  run(() => updateDeliveryClient(id, p), "Unexpected error saving the client.");

export const createPackageAction = (p: FeedPackageCreatePayload) =>
  run(() => createFeedPackage(p), "Unexpected error creating the package.");
export const updatePackageAction = (id: string, p: FeedPackageUpdatePayload) =>
  run(() => updateFeedPackage(id, p), "Unexpected error saving the package.");

export const updateFeedDefinitionAction = (code: string, p: FeedDefinitionUpdatePayload) =>
  run(() => updateFeedDefinition(code, p), "Unexpected error saving the feed query.");

export const createAssignmentAction = (p: DeliveryAssignmentCreatePayload) =>
  run(() => createDeliveryAssignment(p), "Unexpected error creating the assignment.");
export const updateAssignmentAction = (id: string, p: DeliveryAssignmentUpdatePayload) =>
  run(() => updateDeliveryAssignment(id, p), "Unexpected error saving the assignment.");
export const deleteAssignmentAction = (id: string) =>
  run(() => deleteDeliveryAssignment(id), "Unexpected error removing the assignment.");

export const createTransformAction = (p: ClientFeedTransformCreatePayload) =>
  run(() => createClientFeedTransform(p), "Unexpected error creating the transform.");
export const updateTransformAction = (id: string, p: ClientFeedTransformUpdatePayload) =>
  run(() => updateClientFeedTransform(id, p), "Unexpected error saving the transform.");
export const deleteTransformAction = (id: string) =>
  run(() => deleteClientFeedTransform(id), "Unexpected error removing the transform.");
